import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

export interface usePaginationProps<T> {
  items: T[];
  getPaginationItems?: (page: number) => Promise<{ items: T[]; isLoadedAll: boolean }>;
  scrollableRef?: Element | null;
  lastCardRef: RefObject<HTMLDivElement>;
  loadSteps?: number;
}

export function useScrollPagination<T>({
  getPaginationItems,
  items: itemsProp,
  scrollableRef = null,
  lastCardRef,
  loadSteps = 20,
}: usePaginationProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadedItems, setLoadedItems] = useState(loadSteps);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setItems([...itemsProp]);
  }, [itemsProp]);

  const loadMoreCards = useCallback(() => {
    if (allLoaded || isLoading) return;

    if (!getPaginationItems || items.length > loadedItems) {
      if (loadedItems >= items.length) {
        setAllLoaded(true);
      }

      setLoadedItems((previous) => previous + loadSteps);
      setPage((previous) => previous + 1);
    } else {
      setIsLoading(true);
      getPaginationItems(page + 1)
        .then((response) => {
          if (response.isLoadedAll) {
            setAllLoaded(true);
          }

          setItems((previous) => [...previous, ...response.items]);
          setLoadedItems((previous) => previous + response.items.length);
          setPage((previous) => previous + 1);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [allLoaded, isLoading, getPaginationItems, items.length, loadSteps, loadedItems, page]);

  const displayItems = useMemo(() => {
    return items.slice(0, loadedItems);
  }, [items, loadedItems]);

  useEffect(() => {
    if (!lastCardRef.current || items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreCards();
        }
      },
      {
        root: scrollableRef,
        threshold: 0,
      }
    );

    observer.observe(lastCardRef.current);

    return () => observer.disconnect();
  }, [loadMoreCards, lastCardRef, displayItems, scrollableRef, items]);

  return {
    displayItems,
    allLoaded,
  };
}
